// Every Storyteller action, in two rows. Row 1 is the spine: the current phase, one primary button
// for the next step, and the timer, always in the same place. Row 2 holds the contextual tools
// (setup before the reveal, voting during the day); out-of-phase groups recede rather than
// disappear. Disabled buttons explain why on hover.

import { useState, type ReactNode } from "react";
import {
  ChevronDown, Eraser, Eye, Flag, Moon, Shuffle, Sun, Timer, Users, Volume2,
} from "lucide-react";
import type { ClientEventName, GameView } from "@cc/shared";
import { useGame } from "../../state/game.js";
import { cn } from "../../lib/ui.js";
import { EndGameForm } from "./EndGameForm.js";
import { ScriptLibrary } from "./ScriptLibrary.js";
import {
  Button, Dialog, DialogContent, DialogTrigger, Input, Label, Tooltip,
} from "../ui/primitives.js";

/** Whole minutes for the timer's one-click presets. */
const TIMER_PRESETS = [2, 5, 10] as const;

/** Vote clock speeds, in seconds the hand spends on each seat. */
const VOTE_SPEEDS = [
  { label: "Slow", seconds: 5 },
  { label: "Normal", seconds: 3 },
  { label: "Fast", seconds: 1.5 },
] as const;

/** A labelled group. `dim` means "not what you are doing right now" — still live, just quieter. */
function Group({ title, dim = false, children }: { title: string; dim?: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 transition-opacity",
        dim && "opacity-45 focus-within:opacity-100 hover:opacity-100",
      )}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wider text-brass/80">{title}</h3>
      <div className="flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

function Divider() {
  return <div className="h-14 w-px self-center bg-ink-line" />;
}

/** A button that always explains itself, and explains why it is off when it is off. */
function Action({
  label,
  icon,
  tip,
  disabledTip,
  onClick,
  variant = "secondary",
  disabled = false,
}: {
  label: string;
  icon?: ReactNode;
  tip: string;
  /** Shown instead of `tip` when disabled — say why, not what. */
  disabledTip?: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  disabled?: boolean;
}) {
  return (
    <Tooltip content={disabled ? (disabledTip ?? tip) : tip} disabled={disabled}>
      <Button variant={variant} disabled={disabled} onClick={onClick}>
        {icon}
        {label}
      </Button>
    </Tooltip>
  );
}

type NextStep = {
  label: string;
  icon: ReactNode;
  event: ClientEventName;
  tip: string;
  disabledTip?: string;
  disabled: boolean;
};

/**
 * The single "what happens next" action. Its label changes with the phase but its position never
 * does: Start Night → Reveal Roles → End Night → Open Nominations → Start Night → …
 */
function nextStep(view: GameView): NextStep {
  const unassigned = view.seats.filter((s) => !s.isTraveler && !s.characterId).length;

  if (view.phase.kind === "setup") {
    return {
      label: "Start Night",
      icon: <Moon className="size-4" />,
      event: "st:startNight",
      tip: "Begins the first night. Roles stay hidden until you reveal them, so you can adjust after talking to the Demon.",
      disabledTip:
        view.seats.length === 0
          ? "Nobody has joined yet."
          : `${unassigned} seat(s) still have no character — assign them by clicking a seat.`,
      disabled: unassigned > 0 || view.seats.length === 0,
    };
  }
  if (!view.rolesRevealed) {
    return {
      label: "Reveal Roles",
      icon: <Eye className="size-4" />,
      event: "st:revealRoles",
      tip: "Players learn their own character. Nothing before this moment is visible to them.",
      disabled: false,
    };
  }
  if (view.phase.kind === "night") {
    return {
      label: "End Night",
      icon: <Sun className="size-4" />,
      event: "st:endNight",
      tip: "Moves to the day and brings everyone back to the main channel.",
      disabled: false,
    };
  }
  if (view.phase.kind === "day" && view.daySubPhase === "discussion") {
    return {
      label: "Open Nominations",
      icon: <Users className="size-4" />,
      event: "st:openNominations",
      tip: "Lets living players nominate. Until then the day is discussion only.",
      disabled: false,
    };
  }
  return {
    label: "Start Night",
    icon: <Moon className="size-4" />,
    event: "st:startNight",
    tip: "Moves to the night and sends every player to their own Discord channel.",
    disabled: false,
  };
}

function phaseLabel(view: GameView): string {
  if (view.phase.kind === "setup") return "Setup";
  if (view.phase.kind === "night") return `Night ${view.phase.number}`;
  return `Day ${view.phase.number} · ${view.daySubPhase === "nominations" ? "Nominations" : "Discussion"}`;
}

export function ControlPanel({ view }: { view: GameView }) {
  const send = useGame((s) => s.send);
  const [open, setOpen] = useState(true);
  const [minutes, setMinutes] = useState("5");
  const [pullBack, setPullBack] = useState(true);
  const [muteOnReturn, setMuteOnReturn] = useState(false);
  const [secondsPerSeat, setSecondsPerSeat] = useState<number>(3);
  const [endOpen, setEndOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const step = nextStep(view);
  const isDay = view.phase.kind === "day";
  const nominating = isDay && view.daySubPhase === "nominations";
  const openNomination = view.nominations.find((n) => n.id === view.openNominationId) ?? null;
  const todaysNominations = view.nominations.filter((n) => n.day === view.phase.number).length;

  // The hand sweeps every seat, dead included, so this is the number the Storyteller actually
  // cares about — "3" on its own says nothing about how long a vote takes.
  const sweepSeconds = Math.round(view.seats.length * secondsPerSeat);

  function startTimer(): void {
    const value = Number(minutes) || 5;
    // The only difference between these two is whether the bot drags everyone back at the end, so
    // they are one button and a switch rather than two buttons that look alike.
    if (pullBack && isDay) void send("st:startDay", { minutes: value, muteOnReturn });
    else void send("st:startCountdown", { minutes: value, label: "Countdown" });
  }

  return (
    <div className="pointer-events-auto flex flex-col items-stretch">
      {/* The notch: title and toggle in one, so the collapsed state still says what it is. */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={cn(
          "flex w-fit items-center gap-2 self-center rounded-t-lg border border-b-0 border-ink-line",
          "bg-ink-soft/95 px-4 py-1.5 text-sm font-medium text-parchment backdrop-blur",
          "hover:text-brass focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brass",
          !open && "rounded-b-lg border-b",
        )}
      >
        <ChevronDown className={cn("size-4 text-parchment-dim transition", open && "rotate-180")} />
        Storyteller Controls
      </button>

      {open && (
        <div className="rounded-lg border border-ink-line bg-ink-soft/95 px-4 py-3 backdrop-blur">
          {/* --- row 1: the spine, identical in every phase --- */}
          <div className="flex flex-wrap items-start gap-x-5 gap-y-3">
            <div className="flex flex-col gap-1.5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-brass">
                {phaseLabel(view)}
              </h3>
              <Action
                label={step.label}
                icon={step.icon}
                tip={step.tip}
                {...(step.disabledTip !== undefined ? { disabledTip: step.disabledTip } : {})}
                disabled={step.disabled}
                variant="primary"
                onClick={() => void send(step.event, {} as never)}
              />
            </div>

            <Divider />

            <Group title="Timer">
              <div className="flex items-center gap-1">
                {TIMER_PRESETS.map((preset) => (
                  <Button
                    key={preset}
                    size="sm"
                    variant={Number(minutes) === preset ? "primary" : "secondary"}
                    onClick={() => setMinutes(String(preset))}
                  >
                    {preset}m
                  </Button>
                ))}
                <Input
                  aria-label="Minutes"
                  value={minutes}
                  inputMode="decimal"
                  onChange={(e) => setMinutes(e.target.value)}
                  className="w-14 px-2 py-1"
                />
                <span className="text-xs text-parchment-dim">min</span>
              </div>

              <Tooltip
                content={
                  isDay
                    ? "When the timer ends, the bot pulls everyone back to the main channel."
                    : "Only a day timer moves people. During setup or a night this is just a countdown."
                }
                disabled={!isDay}
              >
                <label
                  className={cn(
                    "flex items-center gap-1.5 text-xs text-parchment-dim",
                    !isDay && "cursor-not-allowed opacity-50",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={pullBack && isDay}
                    disabled={!isDay}
                    onChange={(e) => setPullBack(e.target.checked)}
                    className="accent-brass"
                  />
                  pull back to town square at end
                </label>
              </Tooltip>

              <Tooltip
                content="Server-mutes everyone as they arrive, so the day starts in silence."
                disabled={!pullBack || !isDay}
              >
                <label
                  className={cn(
                    "flex items-center gap-1.5 text-xs text-parchment-dim",
                    (!pullBack || !isDay) && "cursor-not-allowed opacity-50",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={muteOnReturn && pullBack && isDay}
                    disabled={!pullBack || !isDay}
                    onChange={(e) => setMuteOnReturn(e.target.checked)}
                    className="accent-brass"
                  />
                  mute on return
                </label>
              </Tooltip>

              <Action
                label="Start Timer"
                icon={<Timer className="size-4" />}
                tip={
                  pullBack && isDay
                    ? "Counts down in the middle of the circle. Nobody is moved until it ends."
                    : "Counts down in the middle of the circle. Nothing happens on Discord."
                }
                onClick={startTimer}
              />
              {view.countdown && (
                <Action
                  label="Stop Timer"
                  tip="Clears the countdown without moving anyone."
                  variant="ghost"
                  onClick={() => void send("st:clearCountdown", {})}
                />
              )}
            </Group>

            <div className="ml-auto self-end">
              <Action
                label="Unmute All"
                icon={<Volume2 className="size-4" />}
                tip="Un-mutes every player at once, for when you are ready to let the day begin."
                onClick={() => void send("st:unmuteAll", {})}
              />
            </div>
          </div>

          <hr className="my-3 border-ink-line" />

          {/* --- row 2: what this phase needs, with the rest dimmed but reachable --- */}
          <div className="flex flex-wrap items-start gap-x-5 gap-y-3">
            <Group title="Game Setup" dim={view.rolesRevealed}>
              <ScriptLibrary view={view} />
              <Action
                label="Randomize Roles"
                icon={<Shuffle className="size-4" />}
                tip="Deals roles by the official player-count distribution. Travelers are left out."
                disabledTip="Roles are already out — reassign a seat by clicking it instead."
                disabled={view.rolesRevealed}
                onClick={() => void send("st:randomize", {})}
              />
              <Action
                label="Randomise Seating"
                icon={<Users className="size-4" />}
                tip="Shuffles who sits where. Everyone keeps the role they were dealt."
                disabledTip={
                  view.openNominationId
                    ? "Finish the open nomination first."
                    : "There is nobody to shuffle yet."
                }
                disabled={view.seats.length < 2 || view.openNominationId !== null}
                onClick={() => void send("st:randomizeSeating", {})}
              />
            </Group>

            <Divider />

            <Group title="Voting" dim={!nominating}>
              <div className="flex items-center gap-1">
                <Label className="whitespace-nowrap">Vote clock</Label>
                {VOTE_SPEEDS.map((speed) => (
                  <Button
                    key={speed.label}
                    size="sm"
                    variant={secondsPerSeat === speed.seconds ? "primary" : "secondary"}
                    onClick={() => setSecondsPerSeat(speed.seconds)}
                  >
                    {speed.label}
                  </Button>
                ))}
                <Tooltip
                  content={`${secondsPerSeat}s per seat across ${view.seats.length} seats — the hand sweeps the whole circle, dead players included.`}
                >
                  <span className="cursor-help text-xs tabular-nums text-parchment-dim underline decoration-dotted underline-offset-2">
                    ≈{sweepSeconds}s
                  </span>
                </Tooltip>
              </div>

              <Action
                label="Start Vote"
                tip="Sends the clock hand round from the nominee. Each seat locks as it passes."
                disabledTip={
                  openNomination ? "That vote has already run." : "Nobody has been nominated yet."
                }
                variant="primary"
                disabled={!openNomination || openNomination.status !== "open"}
                onClick={() =>
                  openNomination &&
                  void send("st:startVote", {
                    nominationId: openNomination.id,
                    secondsPerSeat,
                  })
                }
              />
            </Group>

            {/* --- the things you do not want to click by accident --- */}
            <div className="ml-auto flex items-end gap-1.5 self-end">
              {confirmClear ? (
                <>
                  <Action
                    label="Really clear"
                    tip="Wipes today's nominations and hands back any ghost votes they spent."
                    variant="danger"
                    onClick={() => {
                      void send("st:clearNominations", {}).then(() => setConfirmClear(false));
                    }}
                  />
                  <Button variant="ghost" onClick={() => setConfirmClear(false)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Action
                  label="Clear Nominations"
                  icon={<Eraser className="size-4" />}
                  tip="Starts the day's voting over: wipes today's nominations and refunds any ghost votes. Does not undo an execution."
                  disabledTip="Nothing has been nominated today."
                  disabled={todaysNominations === 0}
                  onClick={() => setConfirmClear(true)}
                />
              )}

              <Dialog open={endOpen} onOpenChange={setEndOpen}>
                <Tooltip content="Records the winner and writes a permanent history record.">
                  <DialogTrigger asChild>
                    <Button variant="danger">
                      <Flag className="size-4" /> End Game
                    </Button>
                  </DialogTrigger>
                </Tooltip>
                <DialogContent
                  title="End the game"
                  description="This writes a permanent history record and cannot be undone."
                >
                  <EndGameForm
                    onEnd={async (result) => {
                      const ack = await send("st:endGame", result);
                      if (ack.ok) setEndOpen(false);
                      return ack.ok;
                    }}
                  />
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
